import { forwardRef, useMemo } from 'react';
import { Input } from '@/components/ui';
import { useLocale, useT } from '@/i18n';
import { callingCodeOf, countryOptions } from '@/lib/country';
import { flagOf, looksLikePhone, splitInternational } from './identifier';

export interface ContactInputProps {
  /** `phone`: a mobile number only. `any`: "Mobile number or email" — the country chip shows while it looks like a number. */
  mode: 'phone' | 'any';
  value: string;
  onChange: (value: string) => void;
  /** The country in the picker (ISO code). */
  country: string;
  onCountryChange: (country: string) => void;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  autoFocus?: boolean;
}

/**
 * A number field with a country-code chip in front (🇮🇳 +91 ▾), like the sign-in boxes parents know
 * from other apps. The chip is a real <select> laid over the badge, so the phone's own picker and
 * type-to-search work. A code typed by hand (+44…) wins over the chip and the chip follows it.
 */
export const ContactInput = forwardRef<HTMLInputElement, ContactInputProps>(
  ({ mode, value, onChange, country, onCountryChange, autoFocus, ...aria }, ref) => {
    const t = useT();
    const { locale } = useLocale();
    const options = useMemo(() => countryOptions(locale), [locale]);
    const showChip = mode === 'phone' || looksLikePhone(value);
    const typed = value.trim().startsWith('+') ? splitInternational(value.replace(/\D/g, ''), country) : undefined;
    const shown = typed?.country ?? country;

    return (
      <div className="flex gap-2">
        {showChip && (
          <div className="relative flex min-h-tap shrink-0 items-center gap-1 rounded-sm border border-sage-300 bg-white px-3 text-body text-ink focus-within:border-teal-500">
            <span aria-hidden="true">{flagOf(shown)}</span>
            <span aria-hidden="true">+{callingCodeOf(shown)}</span>
            <span aria-hidden="true" className="text-ink-muted">
              ▾
            </span>
            <select
              aria-label={t('signin.phoneCountry')}
              value={shown}
              onChange={(e) => {
                onCountryChange(e.target.value);
                // A hand-typed code would override the new pick; keep only the national part.
                if (typed) onChange(typed.national);
              }}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            >
              {options.map((o) => (
                <option key={o.code} value={o.code}>
                  {`${flagOf(o.code)} ${o.name} (+${callingCodeOf(o.code)})`}
                </option>
              ))}
            </select>
          </div>
        )}
        <Input
          ref={ref}
          {...aria}
          className="min-w-0 flex-1"
          type={mode === 'phone' ? 'tel' : 'text'}
          inputMode={mode === 'phone' ? 'tel' : undefined}
          autoComplete={mode === 'phone' ? 'tel-national' : 'username'}
          autoCapitalize="none"
          spellCheck={false}
          autoFocus={autoFocus}
          placeholder={mode === 'phone' ? (shown === 'IN' ? '98765 43210' : '') : t('auth.contactPlaceholder')}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  },
);
ContactInput.displayName = 'ContactInput';
